import { useCallback, useState } from 'react';
import { postingLogApi } from '@/services/postingLogApi';
import { getApiErrorStatus } from '@/services/reportPaging';
import { toast } from 'sonner';
import { describeRetryError } from '@/utils/PostingLog/mappers';

interface UsePostingLogRetryParams {
  allowRetry: boolean;
  integrationConfigured: boolean;
  selectedIds: Set<string>;
  deselect: (rowId: string) => void;
  onRetried: () => void;
}

export function usePostingLogRetry({ allowRetry, integrationConfigured, selectedIds, deselect, onRetried }: UsePostingLogRetryParams) {
  const [retryingIds,      setRetryingIds]      = useState<Set<string>>(new Set());

  // Confirmation dialog
  const [retryDialogOpen, setRetryDialogOpen] = useState(false);
  const [retryTarget,     setRetryTarget]     = useState<string[]>([]);

  const resendLine = useCallback(async (rowId: string): Promise<void> => {
    await postingLogApi.resend(Number(rowId));
    deselect(rowId);
  }, [deselect]);

  const handleRetry = useCallback((rowId: string) => {
    setRetryTarget([rowId]);
    setRetryDialogOpen(true);
  }, []);

  const handleBulkRetry = useCallback(() => {
    if (selectedIds.size === 0) return;
    setRetryTarget([...selectedIds]);
    setRetryDialogOpen(true);
  }, [selectedIds]);

  const handleRetryConfirm = useCallback(async () => {
    const ids = retryTarget;
    if (ids.length === 0 || !allowRetry || !integrationConfigured) return;

    if (ids.length === 1) {
      const id = ids[0];
      setRetryingIds((prev) => new Set([...prev, id]));
      try {
        await resendLine(id);
        toast.success('Line posted successfully');
      } catch (err) {
        toast.error('Retry failed', { description: describeRetryError(err) });
      } finally {
        setRetryingIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
      }
    } else {
      let successCount = 0;
      let lastError: string | undefined;
      for (const id of ids) {
        setRetryingIds((prev) => new Set([...prev, id]));
        try {
          await resendLine(id);
          successCount++;
        } catch (err) {
          lastError = describeRetryError(err);
          if (getApiErrorStatus(err) === 503) break;
        } finally {
          setRetryingIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
        }
      }
      const summary = `${successCount} of ${ids.length} lines posted successfully`;
      if (successCount === ids.length) toast.success(summary);
      else toast.error(summary, { description: lastError });
    }
    setRetryTarget([]);
    onRetried();
  }, [retryTarget, allowRetry, integrationConfigured, resendLine, onRetried]);

  return {
    retryingIds, retryDialogOpen, setRetryDialogOpen, retryTarget,
    handleRetry, handleBulkRetry, handleRetryConfirm,
  };
}
